import React, {useContext, useEffect} from "react";
import {connect} from "react-redux";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import {injectIntl} from "react-intl";

import ContractsContext from "./contractsContext";
import {getFieldValue, getFirstUnfilledCustomField} from "./contractsFieldsFunctions";
import ContractsPopupSignatureField from "./contractsPopupSignatureField";
import ContractsPopupCheckboxField from "./contractsPopupCheckboxField";
import ContractsPopupNextIndicator from "./contractsPopupNextIndicator";
import {changeContractsPage} from "./contractsFunctions";
import ContractsPopupFieldValue from "./contractsPopupFieldValue";
import {setContractsValues} from "../../../actions/contractsActions";
import contractsPopupTextAreaStyles from "./contractsPopupTextAreaStyles";

const yExpand = 6;
let needToScrollToViewAfterPageChange = false;

const ContractsPopupFields = (props) => {

  const {pageRef, contractRef, changeContext, contractsList, setContractsValues, intl, contractsValues,
    grandTotal, depositValue} = props;

  const context = useContext(ContractsContext);
  const {activeContract, pageRendered, activeContractPage, selectedFieldIndex,
    globalContractPage} = context;

  const masterContractFields = activeContract.fields;

  // useEffect(() => {
  //   document.addEventListener("keydown", handleTextFieldOnKeyDown)
  //   return () => {
  //     document.removeEventListener("keydown", handleTextFieldOnKeyDown)
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  useEffect(() => {
    if (pageRendered && needToScrollToViewAfterPageChange) {
      needToScrollToViewAfterPageChange = false;
      scrollToIndicator()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRendered]);

  useEffect(() => {
    if (!pageRendered || selectedFieldIndex === -1) {
      return
    }
    const fieldToFocus = document.getElementById("field" + selectedFieldIndex);
    if (fieldToFocus) {
      fieldToFocus.focus();
    }
  }, [pageRendered, selectedFieldIndex]);

  if (isEmpty(masterContractFields) || !pageRendered || !pageRef || !pageRef.current || !pageRef.current.ref
    || !contractRef || !contractRef.current) {
    return null;
  }

  const handleFieldFocus = (fieldIndex) => {
    changeContext({selectedFieldIndex: fieldIndex});
  }

  const handleTextFieldOnBlur = () => {
    changeContext({selectedFieldIndex: -1});
  }

  const handleTextFieldOnChange = (event, fieldIndex, fieldName) => {
    setContractsValues({
      contractId: activeContract.id,
      pageIndex: activeContractPage,
      fieldIndex,
      name: fieldName,
      value: event.target.value,
    });
  }

  const scrollToIndicator = () => {
    setTimeout(() => {
      const element = document.querySelector(".contract-body-indicator.contract-body-indicator-next");
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      }
    }, 100);
  }

  const handleNextIndicatorClick = () => {
    const { page, fieldIndex } = getFirstUnfilledCustomField(contractsValues, contractsList);
    if (page === -1) {
      return
    }
    const newContext = {
      ...context,
      pageRendered: globalContractPage === page,
      selectedFieldIndex: fieldIndex
    }
    changeContext(newContext);
    changeContractsPage(page, contractsList, newContext, changeContext);
    if (globalContractPage === page) {
      scrollToIndicator();
    } else {
      needToScrollToViewAfterPageChange = true;
    }
  }

  const handleTextFieldOnKeyDown = (event) => {
    if (event.keyCode === 13 && event.target && !event.target.rows) { // textarea
      event.preventDefault();
      event.target.blur();
      // changeContext({ selectedFieldIndex: -1}); // causes a render bug! where showPage is false
    } else if (event.keyCode === 9) {
      event.preventDefault();
      handleNextIndicatorClick();
    }
  }

  let pageOffsetLeft = pageRef.current.ref.offsetLeft;
  let pageOffsetTop = pageRef.current.ref.offsetTop;

  let fieldsCoords = [];
  let renderFields = [];
  let selectedFieldCoords = null;
  let selectedFieldHasValue = null;

  // console.log("masterContractFields", masterContractFields);

  masterContractFields.forEach(field => {
    field.coords.forEach((coord, coordIndex) => {
      if (coord.page !== activeContractPage) {
        return
      }
      // const { fieldIndexOnPage, isRequired } = coord;
      const { fieldIndexOnPage } = coord;
      const { isRequired } = field;
      let { llx, lly, urx, ury } = coord.rectangle;
      const scaleX = pageRef.current.ref.clientWidth / coord.pageRect.width;
      const scaleY = pageRef.current.ref.clientHeight / coord.pageRect.height;
      const top = coord.pageRect.height - ury;
      let fieldCoords = {
        left: pageOffsetLeft + llx * scaleX,
        top: pageOffsetTop + top * scaleY - yExpand / 2,
        width: (urx - llx) * scaleX,
        height: ury - lly + yExpand * scaleY
      };
      if (!field.isEditable) { // here we check if we need to replace with user values
        renderFields.push(<ContractsPopupFieldValue field={field}
                                                    grandTotal={grandTotal}
                                                    depositValue={depositValue}
                                                    fieldCoords={fieldCoords}
                                                    coord={coord}
                                                    key={field.name + coordIndex}/>)
        return;
      }
      const isSelected = fieldIndexOnPage === selectedFieldIndex;
      if (field.isCustom) {
        const isTextArea = field.name === "custom-area" || field.name === "@custom-area";
        const finalStyles = isTextArea
          ? {...fieldCoords, ...contractsPopupTextAreaStyles(coord.lineCount, fieldCoords.height)}
          : fieldCoords;
        const value = getFieldValue(activeContractPage, fieldIndexOnPage, activeContract, contractsValues) || "";
        // console.log("value", value);
        if (isRequired) {
          fieldsCoords.push({ coords: fieldCoords, idx: fieldIndexOnPage });
        }
        if (isSelected) {
          selectedFieldCoords = {...fieldCoords};
          selectedFieldHasValue = !!value;
        }
        if (isTextArea) {
          renderFields.push(
            <textarea className={'regular contract-body__custom-text-field contract-body__custom-text-field--textarea'
              + (isRequired ? ' contract-body__custom-text-field-required' : "")
              + (value ? " contract-body__custom-text-field--with-value" : "")}
                      key={field.name + coordIndex}
                      id={"field" + fieldIndexOnPage}
                      rows={coord.lineCount || 2}
                      data-name={field.name}
                      style={finalStyles}
                      // autoFocus={true}
                      onFocus={() => handleFieldFocus(fieldIndexOnPage)}
                      onBlur={handleTextFieldOnBlur}
                      onChange={(event => handleTextFieldOnChange(event, fieldIndexOnPage, field.name))}
                      onKeyDown={handleTextFieldOnKeyDown}
                      defaultValue={value}/>
          )
        } else {
          renderFields.push(
            <input className={'regular contract-body__custom-text-field'
              + (isRequired ? ' contract-body__custom-text-field-required' : "")
              + (value ? " contract-body__custom-text-field--with-value" : "")}
                   id={"field" + fieldIndexOnPage}
                   key={field.name + coordIndex}
                   data-name={field.name}
                   style={fieldCoords}
                   onFocus={() => handleFieldFocus(fieldIndexOnPage)}
                   onBlur={handleTextFieldOnBlur}
                   onChange={(event => handleTextFieldOnChange(event, fieldIndexOnPage, field.name))}
                   onKeyDown={handleTextFieldOnKeyDown}
                   value={value}/>
          )
        }
      } else if (field.isSignature) {
        if (isRequired) {
          fieldsCoords.push({coords: fieldCoords, idx: fieldIndexOnPage});
        }
        if (isSelected) {
          selectedFieldCoords = {...fieldCoords};
          selectedFieldHasValue = !!getFieldValue(activeContractPage, fieldIndexOnPage, activeContract, contractsValues);
        }
        renderFields.push(
          <ContractsPopupSignatureField key={field.name + coordIndex}
                                        fieldCoords={fieldCoords}
                                        fieldName={field.name}
                                        isRequired={isRequired}
                                        coord={coord}
                                        fieldIndex={fieldIndexOnPage}/>
        )
      } else if (field.isCheckBox) {
        renderFields.push(
          <ContractsPopupCheckboxField key={field.name + coordIndex}
                                       fieldCoords={fieldCoords}
                                       fieldName={field.name}
                                       fieldIndex={fieldIndexOnPage}/>
        )
      }
    })
  });
  if (selectedFieldCoords) {
    renderFields.push(<ContractsPopupNextIndicator top={selectedFieldCoords.top}
                                                   selectedFieldHasValue={selectedFieldHasValue}
                                                   key={"contPopupNextIndicator"}
                                                   text={selectedFieldHasValue
                                                     ? intl.formatMessage({id: "contractSign.next"})
                                                     : null}
                                                   handleNextIndicatorClick={handleNextIndicatorClick}
                                                   pageOffsetLeft={pageOffsetLeft}/>)
  }
  if (!selectedFieldCoords && !isEmpty(fieldsCoords)) {

    if (!selectedFieldCoords ) {
      let firstEmpty = 0;
      let text = intl.formatMessage({ id: "contractSign.fillInText" });
      while (firstEmpty < fieldsCoords.length && getFieldValue(activeContractPage, fieldsCoords[firstEmpty].idx, activeContract, contractsValues)) {
        firstEmpty++;
      }
      if (firstEmpty >= fieldsCoords.length) {
        firstEmpty = fieldsCoords.length - 1;
      }
      if (getFieldValue(activeContractPage, fieldsCoords[firstEmpty].idx, activeContract, contractsValues)) {
        const { page } = getFirstUnfilledCustomField(contractsValues, contractsList);
        text = intl.formatMessage({id: (page !== -1) ? "contractSign.next" : "contractSign.sign" })
      }
      renderFields.push(<ContractsPopupNextIndicator top={fieldsCoords[firstEmpty].coords.top}
                                                     key={"contPopupNextIndicator2"}
                                                     handleNextIndicatorClick={handleNextIndicatorClick}
                                                     text={text}
                                                     pageOffsetLeft={pageOffsetLeft}/>)
    }
  }

  return renderFields
};

ContractsPopupFields.propTypes = {
  changeContext: PropTypes.func,
  grandTotal: PropTypes.number,
  depositValue: PropTypes.number,
}

const mapStateToProps = (store) => {
  const {contractsList, step, contractsValues} = store.contractSigningState;
  const {windowWidth, windowHeight} = store.serverState;

  return {
    contractsList,
    contractsValues,
    step,
    windowWidth, windowHeight, // update on resize
  };
};

const mapDispathToProps = {
  setContractsValues,
}

export default injectIntl(
  connect(mapStateToProps, mapDispathToProps)(ContractsPopupFields)
)
